import React from 'react';
import { Mail, Phone, MapPin, Globe, Palette, Radio } from 'lucide-react';
import './portal.css';

export const NetixLogo = ({ className = 'h-10', chip = true, testId = 'netix-logo' }) => (
  <span className={chip ? 'portal-logo-chip inline-flex items-center' : 'inline-flex items-center'} data-testid={testId}>
    <img src="/netix-logo-horizontal.png" alt="Netix Fibernet" className={`${className} w-auto object-contain`} />
  </span>
);

const CERTS = [
  { src: '/badges/iso-9001-2015.png', title: 'ISO 9001:2015', sub: 'Quality Management System', id: 'iso' },
  { src: '/badges/iso-27001-2022.png', title: 'ISO/IEC 27001:2022', sub: 'Information Security Management', id: 'iso-27001' },
  { src: '/badges/msme.png', title: 'MSME Registered', sub: 'Govt. of India', id: 'msme' },
  { src: '/badges/mca-incorporation.png', title: 'Certificate of Incorporation', sub: 'Ministry of Corporate Affairs', id: 'mca' },
];

export const PortalFooter = ({ version = 'Customer Portal v1.0', testId = 'portal-footer' }) => (
  <footer className="mt-12 mx-4 md:mx-8 mb-6 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 text-white rounded-xl shadow-2xl overflow-hidden" data-testid={testId}>
    <div className="p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="space-y-4">
          <img src="/netix-logo-horizontal.png" alt="Netix Fibernet" className="h-10 w-auto brightness-0 invert" />
          <p className="text-slate-300 text-sm font-medium">Netix World Internet Services Private Limited</p>
          <p className="text-slate-400 text-xs">Providing high-speed fiber internet across India</p>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Mail className="h-5 w-5 text-blue-400" />Support</h3>
          <div className="space-y-2">
            <a href="mailto:care@netixfibernet.com" className="flex items-center gap-2 text-slate-300 hover:text-blue-400 transition-colors text-sm"><span className="text-blue-400">Customer Care:</span> care@netixfibernet.com</a>
            <a href="mailto:data@netixfibernet.com" className="flex items-center gap-2 text-slate-300 hover:text-blue-400 transition-colors text-sm"><span className="text-blue-400">Data Support:</span> data@netixfibernet.com</a>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Phone className="h-5 w-5 text-green-400" />Call Us</h3>
          <div className="space-y-2">
            <a href="tel:+919319919988" className="flex items-center gap-2 text-slate-300 hover:text-green-400 transition-colors text-sm" data-testid={`${testId}-mobile-link`}>📞 +91 93199 19988</a>
            <a href="tel:06276459015" className="flex items-center gap-2 text-slate-300 hover:text-green-400 transition-colors text-sm" data-testid={`${testId}-office-link`}>☎️ Office Phone - 06276 - 459015</a>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><MapPin className="h-5 w-5 text-red-400" />Our Offices</h3>
          <div className="flex flex-wrap gap-2">
            {['Delhi', 'Bihar', 'Haryana', 'Uttar Pradesh'].map((c) => <span key={c} className="px-3 py-1 bg-slate-700 rounded-full text-xs text-slate-300">{c}</span>)}
          </div>
          <a href="https://www.netixfibernet.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm mt-2"><Globe className="h-4 w-4" />www.netixfibernet.com</a>
        </div>
      </div>
    </div>
    <div className="px-8 pb-8" data-testid={`${testId}-certifications-strip`}>
      <div className="border-t border-slate-700 pt-6 flex flex-col md:flex-row md:items-center md:justify-center gap-6 md:gap-12">
        {CERTS.map((c) => (
          <div key={c.id} className="flex items-center gap-3" data-testid={`${testId}-${c.id}-certification`}>
            <img src={c.src} alt={c.title} className="h-20 w-20 object-contain flex-shrink-0" loading="lazy" />
            <div><p className="text-base font-bold text-yellow-400">{c.title}</p><p className="text-sm text-slate-400">{c.sub}</p></div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-slate-900/50 px-8 py-4 border-t border-slate-700">
      <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-slate-400">
        <p>© {new Date().getFullYear()} Netix Fibernet. All rights reserved.</p>
        <p>{version}</p>
      </div>
    </div>
  </footer>
);

export const THEMES = [
  { id: 'blue', label: 'Netix Blue', bg: 'radial-gradient(circle at 30% 30%, #60a5fa, #1e3a8a 70%)' },
  { id: 'red', label: 'Red', bg: 'radial-gradient(circle at 30% 30%, #f87171, #7f1d1d 70%)' },
  { id: 'black', label: 'Black', bg: 'radial-gradient(circle at 30% 30%, #52525b, #000 70%)' },
  { id: 'white', label: 'White', bg: 'radial-gradient(circle at 30% 30%, #ffffff, #94a3b8 70%)' },
  { id: 'yellow', label: 'Yellow', bg: 'radial-gradient(circle at 30% 30%, #fde68a, #b45309 70%)' },
  { id: 'pink', label: 'Pink', bg: 'radial-gradient(circle at 30% 30%, #f9a8d4, #9d174d 70%)' },
];
export const THEME_KEY = 'portalColorTheme';
export const useColorTheme = () => {
  const [theme, setTheme] = React.useState(() => localStorage.getItem(THEME_KEY) || 'blue');
  React.useEffect(() => { localStorage.setItem(THEME_KEY, theme); }, [theme]);
  return [theme, setTheme];
};

export const ThemePicker = ({ theme, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const cur = THEMES.find((t) => t.id === theme) || THEMES[0];
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg hover:bg-white/15 flex items-center gap-1" aria-label="Choose colour theme" title={`Theme: ${cur.label}`} data-testid="theme-picker-btn">
        <span className="portal-theme-dot !w-5 !h-5" style={{ background: cur.bg }} /><Palette className="h-4 w-4 hidden sm:block" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 z-50 rounded-2xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 shadow-2xl p-3" data-testid="theme-picker-panel">
          <p className="text-[10px] font-bold uppercase text-slate-500 mb-2 whitespace-nowrap">Portal colour theme</p>
          <div className="flex gap-2">{THEMES.map((t) => <button key={t.id} onClick={() => { onChange(t.id); setOpen(false); }} className={`portal-theme-dot ${theme === t.id ? 'active' : ''}`} style={{ background: t.bg }} title={t.label} aria-label={t.label} data-testid={`theme-${t.id}`} />)}</div>
        </div>
      )}
    </div>
  );
};

export const Marquee = ({ data, testId = 'live-marquee' }) => {
  if (!data?.active || !data?.text) return null;
  const speed = { slow: '45s', normal: '28s', fast: '16s' }[data.speed] || '28s';
  return (
    <div className="portal-marquee mb-6 flex items-center" data-testid={testId} role="status" aria-live="polite">
      <span className="shrink-0 px-3 py-2 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 bg-black/20"><Radio className="h-3.5 w-3.5 animate-pulse" />Live</span>
      <div className="flex-1 overflow-hidden py-2"><span className="portal-marquee-track text-sm font-semibold" style={{ '--marquee-speed': speed }} data-testid={`${testId}-text`}>{data.text}</span></div>
    </div>
  );
};

export const WelcomeBanner = ({ name, role, children, testId = 'welcome-banner' }) => (
  <div className="portal-card rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-900 border border-blue-200 dark:border-slate-800 p-4 mb-6" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <NetixLogo className="h-12" />
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Welcome, {name || '…'}!</h3>
          {role && <p className="text-sm text-gray-600 dark:text-slate-400">Role: <span className="font-semibold text-blue-600 dark:text-blue-300">{role}</span></p>}
        </div>
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  </div>
);
